<template>
  <header
    :class="{ 'navigation-bar--with-side-menu': menuOpened }"
    class="navigation-bar"
  >
    <div class="columns align-center is-gapless is-mobile">
      <div class="column is-narrow menu-navigator cursor-pointer">
        <div
          class="menu-switch"
          @click.stop="$emit('menuEvent')"
        >
          <span class="hamburger-custom-icon" />
        </div>
      </div>
      <div class="column">
        <div v-if="!!navigationTitle">
          <div class="navigation-title is-flex align-center is-size-6-desktop is-size-7-touch">
            <a
              class="detail-title has-margin-right-2"
              :class="{ 'disabled': !hasNavigationTitleLink }"
              @click="hasNavTitleRoute && goToTitleRoute()"
            >
              {{ $filters.capitalize(navigationTitle) }}
            </a>
            <LinkButton
              v-if="$route.meta.hasChangeCustomer"
              :label="$t('commons.changeCustomer')"
              :icon-size="15"
              label-classes="has-text-weight-medium is-size-11"
              color="#FF2D55"
              icon="replace"
              class="is-hidden-mobile"
              @click="$bus.emit('changeCustomer')"
            />
            <IconButton
              icon="replace"
              color="#FF2D55"
              background="transparent"
              class="is-hidden-tablet"
              @click="$bus.emit('changeCustomer')"
            />
          </div>
        </div>
        <div
          v-else-if="$route.meta.hasSearchBar"
          class="has-margin-left-2 columns is-gapless is-mobile"
        >
          <SearchInput
            v-model="searchModel"
            class="column is-5-desktop is-9-touch"
            @search-submit="handleSearch"
          />
          <div
            v-if="hasAdvancedSearch"
            class="column is-size-10 is-size-8-touch advanced-search-button"
            @click.stop="$bus.emit('advancedSearch')"
          >
            <i class="icon-equalizer" />
            <span class="has-padding-left-1 has-text-weight-medium">
              {{ $t('comps.NavigationBar.advancedSearch') }}
            </span>
          </div>
        </div>
        <div
          v-if="$route.name === 'DashboardIndex'"
          class="columns is-mobile justify-end has-margin-right-1"
        >
          <Dropdown
            v-if="warehousesDropdown?.length > 1 && user?.profile?.sltfil==='S'"
            v-model="selectedWarehouse"
            :options="warehousesDropdown"
            :line-height="30"
            appearance="secondary"
            class="column is-3-desktop is-4-tablet is-6-mobile"
            @change="handleWarehousesDropdown"
          />
          <Dropdown
            v-if="agentsDropdown?.length > 1"
            v-model="selectedAgents"
            :options="agentsDropdown"
            :line-height="30"
            appearance="secondary"
            class="column is-3-desktop is-4-tablet is-6-mobile "
            @change="handleDropdown"
          />
        </div>
      </div>
      <div
        v-if="hasCart"
        :class="{ 'cursor-pointer': $route.query.fromSection || lastCartPath }"
        class="cart-box column is-narrow"
        @click.prevent="goToCart"
      >
        <span class="cart-count">{{ cart.count || 0 }}</span>
        <i class="icon-cart" />
      </div>
    </div>
  </header>
</template>

<script>
import IconButton from '@/components/buttons/IconButton.vue';
import LinkButton from '@/components/buttons/LinkButton.vue';
import SearchInput from '@/components/search/SearchInput.vue';
import Dropdown from '@/components/dropdown/Dropdown.vue';
import comparator from '@/services/comparator';
import { mapState, mapActions } from 'pinia';
import { useRootStore } from '@/stores/root';
import { useCustomersStore } from '@/stores/customers';

export default {
  name: 'NavigationBar',
  components: {
    SearchInput,
    IconButton,
    LinkButton,
    Dropdown,
  },
  emits: ['menuEvent', 'searchSubmit'],
  data() {
    return {
      searchModel: this.searchModel,
      agents: [],
      agentsDropdown: [],
      selectedAgents: 'choose',
      warehouse: [],
      warehousesDropdown: [],
      selectedWarehouse: 'choose',

    };
  },
  computed: {
    // Vuex mapGetters → Pinia rootStore.
    ...mapState(useRootStore, [
      'detail',
      'navigationTitle',
      'cart',
      'searchText',
      'hasList',
      'hasAdvancedSearch',
      'lastCustomer',
      'sideMenu',
      'menuOpened',
      'user',
      'agent',
      'lastCartPath',
      'hasNavigationTitleLink',
      'warehouseCustomerSelected',
    ]),
    hasNavTitleRoute() {
      return !!this.$route.meta.navTitleRoute;
    },
    hasCart() {
      return this.cart && this.$route.meta.hasCart && this.$route.meta.showCart();
    },
  },
  watch: {
    searchText(newVal) {
      this.searchModel = newVal;
    },
    agent(newVal) {
      if (!newVal) {
        this.selectedAgents = 'choose';
      }
    },
    warehouseCustomerSelected(newVal) {
      if (!newVal) {
        this.selectedWarehouse = 'choose';
      }
    },
    lastCustomer(newVal, oldVal) {
      if (newVal && oldVal && newVal.anacod !== oldVal.anacod) {
        this.setLastCartPath(null);
      }
    },
  },
  async created() {
    this.agents = await this.getAgentsCustomer(true);
    this.agentsDropdown = this.agents.map(item => ({
      name: item.usrage,
      value: item.desage,
    })).sort((a, b) => comparator.compareLiteral(a.value, b.value));
    this.agentsDropdown.splice(0, 0, { name: 'choose', value: this.$t('comps.NavigationBar.chooseAgent') });
    this.warehouse = await this.getWarehousesCustomer(true);
    this.warehousesDropdown = this.warehouse.map(item => ({
      name: item.codmag,
      value: item.desmag,
    }));
    this.warehousesDropdown.splice(0, 0, { name: 'choose', value: this.$t('comps.NavigationBar.chooseWarehouse') });

  },
  methods: {
    // Vuex mapActions → Pinia: azioni su rootStore + customersStore.
    ...mapActions(useRootStore, [
      'setSectionCode',
      'setAgent',
      'setWarehouseCustomerSelected',
      'setLastCartPath',
    ]),
    ...mapActions(useCustomersStore, [
      'getAgentsCustomer',
      'getWarehousesCustomer',
    ]),
    handleSearch(query) {
      this.$emit('searchSubmit', query);
    },
    goBack() {
      this.$router.push(this.detail.back);
    },
    goToTitleRoute() {
      if (this.$route.meta.navTitleRoute === 'CustomerDetail') {
        this.setSectionCode(this.sideMenu.find(item => item.name === 'customers').code);
        this.$router.push(`/customers/${this.lastCustomer.anacod}/card`);
      }
    },
    handleDropdown(item) {
      const agent = this.agents.find(field => field.usrage === item.name);
      this.setAgent(agent);
    },
    handleWarehousesDropdown(item) {
      const warehouse = this.warehouse.find(field => field.codmag === item.name);
      this.setWarehouseCustomerSelected(warehouse?.codmag);
    },
    goToCart() {
      if (this.$route.query.fromSection) {
        this.$router.push(`${this.$route.query.fromSection}/${this.$route.query.cart}/cart`);
      } else if (this.lastCartPath && this.cart) {
        this.$router.push(`${this.lastCartPath}/${this.cart.documentId}/cart`);
      }
    },
  },
};
</script>

<style lang="scss">
.icons-box {
  display: flex;
}

.navigation-bar {
  transition: left .33s ease-in-out;
  background-color: $white;
  border-bottom: 1px solid $white-antiflash;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 25;
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &--with-side-menu {
    @include desktop {
      left: 260px;
    }
  }

  .menu-switch {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;

    &:hover {
      .hamburger-custom-icon {
        background-color: $phosphorescent-red;

        &:before {
          background-color: $phosphorescent-red;
        }

        &:after {
          background-color: $phosphorescent-red;
        }
      }
    }
  }

  .menu-switch .hamburger-custom-icon {
    position: relative;
    width: 20px;
    background-color: $black-gunmetal;
    border-radius: 10px;
    display: block;
    height: 2px;

    &:before {
      content: '';
      position: absolute;
      width: 20px;
      height: 2px;
      background-color: $black;
      border-radius: 10px;
      bottom: 6px;
    }

    &:after {
      content: '';
      position: absolute;
      width: 12px;
      height: 2px;
      background-color: $black;
      border-radius: 10px;
      top: 6px;

    }
  }


  .menu-navigator {
    width: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .menu-navigator .icon {
    color: $white;
    font-size: 20px;
  }

  .navigation-title {
    @include mobile {
      justify-content: space-between;
    }

    .detail-title {
      color: $black-gunmetal;
      font-size: $size-7;
      font-weight: $weight-medium;

      @include mobile {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        max-width: 250px;
      }

      &:hover {
        color: $phosphorescent-red;
      }
    }
  }


  .advanced-search-button {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    cursor: pointer;
    color: $white;
    padding: 2px 0;

    @include desktop {
      position: absolute;
      bottom: 8px;
    }
  }
}

.cart-box {
  display: flex;
  margin-right: 12px !important;
  align-items: center;
  height: 2.125rem;
  width: 2.125rem;
  justify-content: center;
  background-color: $blue-sky;
  border-radius: 50%;
  position: relative;
  box-shadow: 0 0 8px 0 rgba(0, 122, 255, 0.20);

  &:hover {
    transform: scale(1.1);
    transition: transform 0.2s ease-in-out;
  }

  .icon-cart {
    display: inline-flex;
    font-size: $size-7;
    color: $white;
  }

  .cart-count {
    position: absolute;
    color: $white;
    top: -4px;
    right: -4px;
    height: 0.875rem;
    width: 0.875rem;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $phosphorescent-red;
    border-radius: 50%;
    font-size: 0.5rem;
    font-weight: $weight-medium;
    box-shadow: -1px 1px 2px 0 rgba(0, 0, 0, 0.15);
  }

}
</style>
